/* eslint-disable react-refresh/only-export-components -- entry file, never HMR'd */
import { lazy, StrictMode, Suspense, useEffect, useState, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import AppRouter from './AppRouter.tsx'
import { registerWebMcpTools } from './lib/web-mcp'
import { getConsentPreferences } from './firebase'

// WebMCP — register browser-side tools that wrap the public read-only
// content surfaces. Runs once in the browser; no-op on SSR / older browsers.
if (typeof window !== 'undefined') {
  if (document.readyState === 'complete') {
    registerWebMcpTools()
  } else {
    window.addEventListener('load', () => registerWebMcpTools(), { once: true })
  }
}

/**
 * PostHog configuration for the marketing landing.
 *
 * Lighthouse showed the default PostHog SDK pulls in 4 extra modules
 * (surveys.js 32.7 KiB, dead-clicks-autocapture.js 5.5 KiB, web-vitals.js
 * 3.1 KiB, plus a per-project config.js) that block the critical path on
 * mobile and contribute ~1.8 s to LCP. None of them are needed on a public
 * marketing site, so we explicitly disable them.
 */
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  capture_pageview: 'history_change',
  disable_session_recording: true,
  disable_surveys: true,
  // Drop the dead-clicks autocapture extension — irrelevant for SEO surface.
  enable_dead_clicks_autocapture: false,
  // Don't pull web-vitals.js — Firebase Performance + Lighthouse cover this.
  capture_performance: false,
  // Reduce noise: only capture clicks + submits, not the full autocapture
  // event surface. The `as` cast is needed because the SDK types want a
  // mutable array; we keep the parent object readonly via `as const`.
  autocapture: {
    dom_event_allowlist: ['click', 'submit'] as Array<'click' | 'submit'>,
  },
} as const

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
const PostHogProvider = lazy(() =>
  import('@posthog/react').then((module) => ({ default: module.PostHogProvider })),
)

/**
 * Reads current analytics consent from localStorage (opt-in, GDPR).
 * PostHog is an analytics processor, so it must not load until the user has
 * explicitly granted analytics consent via the cookie banner.
 */
function hasAnalyticsConsent(): boolean {
  return getConsentPreferences()?.analytics === true
}

/**
 * Mounts the PostHog provider only after BOTH:
 *  1. the user has granted analytics consent (opt-in, GDPR), and
 *  2. the page is interactive — so the SDK + its dynamically-loaded
 *     extensions never sit in the critical render path.
 *
 * Without consent, PostHog is never initialized and no analytics cookies are
 * set. Consent changes from the cookie banner (which dispatch the
 * `cookieConsentUpdated` event) mount/keep the provider live without a reload.
 */
function DeferredPostHogProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [consented, setConsented] = useState(() => hasAnalyticsConsent())

  useEffect(() => {
    let cancelled = false
    const arm = () => {
      if (cancelled) return
      setReady(true)
    }
    type IdleCallback = (cb: () => void, opts?: { timeout?: number }) => number
    const win = window as unknown as { requestIdleCallback?: IdleCallback }
    if (typeof win.requestIdleCallback === 'function') {
      win.requestIdleCallback(arm, { timeout: 2000 })
    } else {
      // Safari / older Firefox — fall back to a short timeout.
      setTimeout(arm, 1500)
    }
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onConsentUpdated = () => setConsented(hasAnalyticsConsent())
    window.addEventListener('cookieConsentUpdated', onConsentUpdated)
    return () => window.removeEventListener('cookieConsentUpdated', onConsentUpdated)
  }, [])

  if (!ready || !posthogToken || !consented) {
    return <>{children}</>
  }
  return (
    <Suspense fallback={<>{children}</>}>
      <PostHogProvider apiKey={posthogToken} options={posthogOptions}>
        {children}
      </PostHogProvider>
    </Suspense>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Missing #root mount point')
const snapshotRoot: HTMLElement = rootElement

const hasPrerenderedSnapshot = snapshotRoot.hasChildNodes()
const liveRoot = hasPrerenderedSnapshot
  ? document.createElement('div')
  : snapshotRoot

if (hasPrerenderedSnapshot) {
  liveRoot.id = 'root-live'
  liveRoot.hidden = true
  snapshotRoot.after(liveRoot)
}

const routeHeadSnapshot = Array.from(
  document.head.querySelectorAll('[data-route-head]'),
)

/**
 * Keep the complete prerendered document visible until the lazy route and its
 * Helmet metadata have both finished. A failed/blocked chunk therefore leaves
 * usable content and route-specific metadata instead of a blank root.
 */
function commitLiveDocument() {
  const h1 = liveRoot.querySelector('h1')?.textContent?.trim()
  const title = document.head.querySelector('title:not([data-route-head])')
  const description = document.head.querySelector(
    'meta[name="description"]:not([data-route-head])',
  )
  const robots = document.head.querySelector(
    'meta[name="robots"]:not([data-route-head])',
  )
  const canonical = document.head.querySelector(
    'link[rel="canonical"]:not([data-route-head])',
  )
  const isNoindex = robots?.getAttribute('content')?.includes('noindex')

  if (!h1 || !title || !description || !robots || (!canonical && !isNoindex)) {
    return
  }

  routeHeadSnapshot.forEach((element) => element.remove())
  if (hasPrerenderedSnapshot) {
    snapshotRoot.remove()
    liveRoot.id = 'root'
    liveRoot.hidden = false
  }
  readinessObserver.disconnect()
}

const readinessObserver = new MutationObserver(commitLiveDocument)
readinessObserver.observe(liveRoot, { childList: true, subtree: true })
readinessObserver.observe(document.head, {
  attributes: true,
  childList: true,
  subtree: true,
})

createRoot(liveRoot).render(
  <StrictMode>
    <DeferredPostHogProvider>
      <HelmetProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </HelmetProvider>
    </DeferredPostHogProvider>
  </StrictMode>,
)

queueMicrotask(commitLiveDocument)
